/**
 * SwapKpiModal — replaces a widget on the dashboard with one from the
 * `hidden_widgets` pool (same kind only). Backend:
 *   GET  /api/dashboards/{id}/hidden-widgets?kind=<kpi_card|line|bar|donut|table>
 *   POST /api/dashboards/{id}/widgets/{wid}/swap  body: { replacement_id }
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2, Replace, Sparkles } from "lucide-react";
import { recordKpiSwapped } from "@/lib/playbookSignals";

function kindFor(widget) {
    if (!widget) return null;
    if (widget.type === "kpi_card") return "kpi_card";
    if (widget.type === "table")    return "table";
    return (widget.config || {}).chart_type || "bar";
}

const KIND_LABEL = {
    kpi_card: "KPI cards",
    line: "Line charts",
    bar:  "Bar charts",
    donut:"Donut charts",
    table:"Tables",
};

export default function SwapKpiModal({ dashboardId, dashboard, widget, onClose, onSwapped }) {
    const open = !!widget;
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const kind = kindFor(widget);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        api.get(`/dashboards/${dashboardId}/hidden-widgets`, { params: { kind } })
            .then((r) => setItems(r.data?.hidden_widgets || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, [open, dashboardId, kind]);

    const swap = async (replacement) => {
        setBusyId(replacement.id);
        try {
            const r = await api.post(`/dashboards/${dashboardId}/widgets/${widget.id}/swap`, {
                replacement_id: replacement.id,
            });
            toast.success(`Replaced "${widget.title}" with "${replacement.title}"`);
            // iter-119bk Phase 10 — emit kpi_renamed signal so SA can
            // see which widgets users consistently swap to a specific
            // replacement (= the canonical playbook should swap the
            // default).
            if (dashboard) {
                recordKpiSwapped(dashboard, widget, replacement);
            }
            onSwapped?.(r.data);
            onClose?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Swap failed");
        } finally { setBusyId(null); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-2xl" data-testid="swap-kpi-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Replace className="h-4 w-4 text-primary" />
                        Swap "{widget?.title}"
                    </DialogTitle>
                    <DialogDescription className="text-xs">
                        Replace this {KIND_LABEL[kind]?.slice(0, -1).toLowerCase() || "widget"} with another from the auto-generated pool.
                        {" "}Layout and pin status are preserved.
                    </DialogDescription>
                </DialogHeader>
                <div className="max-h-[440px] overflow-y-auto space-y-2 pr-1">
                    {loading && (
                        <div className="text-sm text-muted-foreground py-12 text-center">
                            <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading candidates…
                        </div>
                    )}
                    {!loading && items.length === 0 && (
                        <div className="text-sm text-muted-foreground py-12 text-center" data-testid="swap-empty">
                            <Sparkles className="h-6 w-6 mx-auto mb-2 opacity-40" />
                            No alternative {KIND_LABEL[kind]?.toLowerCase() || "widgets"} available.
                            <p className="text-xs mt-2">Click <b>Regenerate</b> on the dashboard to refresh the auto pool.</p>
                        </div>
                    )}
                    {items.map((it) => {
                        const cfg = it.config || {};
                        const meta = [];
                        if (cfg.column) meta.push(`column: ${cfg.column}`);
                        if (cfg.x) meta.push(`x: ${cfg.x}`);
                        if (cfg.y) meta.push(`y: ${cfg.y}`);
                        if (cfg.agg) meta.push(`agg: ${cfg.agg}`);
                        return (
                            <button key={it.id} onClick={() => swap(it)} disabled={busyId === it.id}
                                className="w-full text-left border border-border rounded-sm bg-card p-3 hover:border-primary/60 hover:bg-secondary/30 transition-colors flex items-start gap-3"
                                data-testid={`swap-candidate-${it.id}`}>
                                <div className="w-9 h-9 bg-primary/10 text-primary rounded-sm flex items-center justify-center shrink-0">
                                    <Replace className="h-4 w-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm">{it.title}</div>
                                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                        <Badge variant="outline" className="font-mono text-[9px]">{it.type}</Badge>
                                        {cfg.chart_type && <Badge variant="outline" className="font-mono text-[9px]">{cfg.chart_type}</Badge>}
                                        {meta.length > 0 && <span className="text-[10px] font-mono text-muted-foreground">{meta.join(" · ")}</span>}
                                    </div>
                                </div>
                                {busyId === it.id && <Loader2 className="h-4 w-4 animate-spin shrink-0" />}
                            </button>
                        );
                    })}
                </div>
            </DialogContent>
        </Dialog>
    );
}
